import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';

/** A user-selectable colour mode. `system` follows the OS preference. */
export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'll-xceed-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * Owns the app's light/dark colour mode.
 *
 * The chosen {@link ThemeMode} is persisted to `localStorage`; `system` tracks
 * the OS `prefers-color-scheme` and updates live. The resolved theme is applied
 * as `data-bs-theme` on `<html>`, which Bootstrap 5.3 uses to recolour the UI.
 * {@link setup} is invoked once from the theme instance initializer so the
 * attribute is set before the app first renders.
 */
export default class ThemeService extends Service {
  /** The user's chosen mode. Defaults to `system` until they pick one. */
  @tracked mode: ThemeMode = 'system';
  @tracked private systemPrefersDark = false;

  private mediaQuery?: MediaQueryList;

  private handleSystemChange = (event: MediaQueryListEvent): void => {
    this.systemPrefersDark = event.matches;
    this.apply();
  };

  /**
   * Load the saved preference, start watching the OS, and apply the theme.
   * Safe to call more than once — the instance initializer calls it once per
   * app instance, but re-syncing from storage on a later call is also
   * legitimate. Only the `matchMedia` listener is one-time: that part alone is
   * guarded, so a repeat call can't attach a second `change` listener.
   */
  setup(): void {
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem(STORAGE_KEY);
    } catch {
      // localStorage can throw in private mode; fall back to `system`.
    }
    this.mode = isThemeMode(stored) ? stored : 'system';

    if (!this.mediaQuery && typeof window.matchMedia === 'function') {
      this.mediaQuery = window.matchMedia(DARK_QUERY);
      this.mediaQuery.addEventListener('change', this.handleSystemChange);
    }
    if (this.mediaQuery) {
      this.systemPrefersDark = this.mediaQuery.matches;
    }

    this.apply();
  }

  /** The theme actually shown, resolving `system` to `light` or `dark`. */
  get resolvedTheme(): 'light' | 'dark' {
    if (this.mode === 'system') {
      return this.systemPrefersDark ? 'dark' : 'light';
    }
    return this.mode;
  }

  /** Set and persist the mode, then re-apply. Bound for use as an action. */
  setMode = (mode: ThemeMode): void => {
    this.mode = mode;
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Ignore persistence failures; the in-memory choice still applies.
    }
    this.apply();
  };

  private apply(): void {
    document.documentElement.setAttribute('data-bs-theme', this.resolvedTheme);
  }

  willDestroy(): void {
    super.willDestroy();
    this.mediaQuery?.removeEventListener('change', this.handleSystemChange);
  }
}
